import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import api, { formatApiError, fmtMoney, tipoLabel } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldCheck,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { goToSatVisorForUuid } from "@/lib/satVisorNavigation";
import { resolveDatosMaestrosAwareBreadcrumb } from "@/modules/datosMaestros/datosMaestrosNavigation";

const YEAR_OPTS = () => {
  const y = new Date().getFullYear();
  return [y, y - 1, y - 2, y - 3, y - 4];
};

export default function AuditoriaEstatusLocal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [autoVerify, setAutoVerify] = useState(true);
  const [audit, setAudit] = useState(null);
  const [busyList, setBusyList] = useState(false);
  const [busyVerify, setBusyVerify] = useState(false);
  const autoRanRef = useRef(false);

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  const loadList = useCallback(async () => {
    if (!clientId) {
      setAudit(null);
      return null;
    }
    setBusyList(true);
    try {
      const { data } = await api.get("/cfdi/auditoria-estatus-local", {
        params: {
          client_id: clientId,
          year: year ? Number(year) : undefined,
        },
        timeout: 120000,
      });
      setAudit(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar la auditoría");
      setAudit(null);
      return null;
    } finally {
      setBusyList(false);
    }
  }, [clientId, year]);

  const runVerify = useCallback(async (silent = false) => {
    if (!clientId) {
      toast.error("Seleccione un cliente");
      return;
    }
    setBusyVerify(true);
    try {
      const { data } = await api.post(
        "/cfdi/sat-verify",
        {
          client_id: clientId,
          year: year ? Number(year) : undefined,
          auto_update: true,
          only_cancelado_local_pendiente: true,
        },
        { timeout: 600000, headers: { "Content-Type": "application/json" } },
      );
      const actualizados = data?.actualizados || 0;
      const verificados = data?.verificados || 0;
      if (!silent) {
        if (actualizados > 0) {
          toast.success(`SAT verificó ${verificados} CFDI · ${actualizados} corregido(s) en la copia local.`);
        } else if (verificados === 0) {
          toast.success("No hay CFDI cancelados locales pendientes de verificar.");
        } else {
          toast.info(`SAT verificó ${verificados} CFDI · el estatus local coincide o no requirió cambio.`);
        }
      }
      await loadList();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo verificar en SAT");
    } finally {
      setBusyVerify(false);
    }
  }, [clientId, year, loadList]);

  useEffect(() => {
    autoRanRef.current = false;
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!autoVerify || autoRanRef.current || busyList || busyVerify) return;
    if (!audit?.items?.length) return;
    autoRanRef.current = true;
    runVerify(true).then(() => {
      const n = audit?.items?.length || 0;
      toast.info(`Verificación automática: ${n} CFDI consultado(s) en SAT. Revise el listado actualizado.`);
    });
  }, [audit, autoVerify, busyList, busyVerify, runVerify]);

  const items = audit?.items || [];
  const folioLabel = (r) => {
    const s = (r.serie || "").trim();
    const f = (r.folio || "").trim();
    if (s && f) return `${s}-${f}`;
    return f || s || "—";
  };

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="auditoria-estatus-local-page">
      <PageHeader
        breadcrumb={resolveDatosMaestrosAwareBreadcrumb(
          location,
          VISOR_MODULE_BREADCRUMB_ROOT,
          "Auditoría estatus local",
        )}
        title="Auditoría estatus local (cancelados)"
        subtitle="CFDI marcados como cancelados en la copia local que aún no fueron confirmados por verificación SAT en vivo. No altera el flujo normal del visor."
      />

      <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
        <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950 flex gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="m-0">
            Use esta pestaña para detectar falsos cancelados (carpeta, metadatos o marca manual).
            Si el SAT responde <strong>Vigente</strong>, el sistema corrige el estatus local automáticamente.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-4 rounded-md border border-slate-200 bg-white p-4">
          <div className="space-y-1 min-w-[200px]">
            <Label>Cliente</Label>
            <Select value={clientId || ""} onValueChange={setClientId}>
              <SelectTrigger data-testid="auditoria-estatus-client">
                <SelectValue placeholder="Seleccione cliente" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.razon_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Año</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28" data-testid="auditoria-estatus-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTS().map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <Checkbox
              id="auto-verify-estatus"
              checked={autoVerify}
              onCheckedChange={(v) => setAutoVerify(v === true)}
              data-testid="auditoria-estatus-auto"
            />
            <Label htmlFor="auto-verify-estatus" className="text-sm font-normal cursor-pointer">
              Verificar automáticamente al cargar
            </Label>
          </div>
          <div className="flex gap-2 pb-0.5 ml-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!clientId || busyList}
              onClick={() => loadList()}
              data-testid="auditoria-estatus-refresh"
            >
              {busyList ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-1">Actualizar listado</span>
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!clientId || busyVerify || !items.length}
              onClick={() => runVerify(false)}
              data-testid="auditoria-estatus-verify"
            >
              {busyVerify ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              <span className="ml-1">Verificar en SAT</span>
            </Button>
          </div>
        </div>

        {audit && (
          <p className="text-sm text-slate-600">
            {audit.total === 0 ? (
              "Sin CFDI cancelados locales pendientes de verificar SAT."
            ) : (
              <>
                <strong>{audit.total}</strong> CFDI pendiente(s)
                {audit.mostrados < audit.total ? ` (mostrando ${audit.mostrados})` : ""}
                {audit.client_name ? ` · ${audit.client_name}` : ""}
                {audit.year ? ` · ${audit.year}` : ""}
              </>
            )}
          </p>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Folio</th>
                  <th className="px-3 py-2 text-left">UUID</th>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-left">Origen cancelado</th>
                  <th className="px-3 py-2 text-left">Última verif.</th>
                  <th className="px-3 py-2 text-left">Acc.</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.uuid || r.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                    <td className="px-3 py-2 font-medium text-slate-800">{folioLabel(r)}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{r.uuid?.slice(0, 8)}…</td>
                    <td className="px-3 py-2">{(r.fecha || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">{tipoLabel(r.tipo_comprobante)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.total)}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-amber-800">
                        <AlertTriangle className="h-3 w-3" />
                        {r.cancel_source_label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {r.verificado_sat_at ? r.verificado_sat_at.slice(0, 16).replace("T", " ") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => goToSatVisorForUuid(navigate, r.client_id, r.uuid).catch((e) => {
                          toast.error(e.message || "No se pudo abrir en visor");
                        })}
                      >
                        Visor
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
